/**
 * DXF 그룹 코드 토크나이저 (작업지시서 P3-02)
 *
 * ASCII DXF 는 **두 줄이 한 쌍**이다:
 *   첫 줄 = 그룹 코드(정수), 둘째 줄 = 값
 *
 *   0
 *   LINE
 *   8
 *   A-WALL
 *   10
 *   1000.0
 *
 * 실전에서 반드시 처리해야 하는 것들:
 *   · 줄바꿈이 CRLF / LF / CR 섞여 들어온다
 *   · 코드 줄에 앞뒤 공백이 붙어 있다 (AutoCAD 는 우측 정렬로 쓴다: "  10")
 *   · 값 줄의 앞뒤 공백은 **의미가 있을 수 있다** (TEXT 내용) → trim 하지 않는다
 *   · 파일 끝에 EOF 가 없거나 잘려 있다
 */

export interface DxfPair {
  code: number;
  value: string;
}

/** 그룹 코드의 값 타입 (DXF 규격의 코드 범위 규칙) */
export type DxfValueKind = 'string' | 'number' | 'int' | 'binary' | 'bool';

export function valueKindOf(code: number): DxfValueKind {
  if (code >= 0 && code <= 9) return 'string';
  if (code >= 10 && code <= 59) return 'number';
  if (code >= 60 && code <= 79) return 'int';
  if (code >= 90 && code <= 99) return 'int';
  if (code === 100 || code === 102 || code === 105) return 'string';
  if (code >= 110 && code <= 149) return 'number';
  if (code >= 160 && code <= 179) return 'int';
  if (code >= 210 && code <= 239) return 'number';
  if (code >= 270 && code <= 289) return 'int';
  if (code >= 290 && code <= 299) return 'bool';
  if (code >= 300 && code <= 369) return 'string';
  if (code >= 370 && code <= 389) return 'int';
  if (code >= 390 && code <= 399) return 'string';
  if (code >= 400 && code <= 409) return 'int';
  if (code >= 410 && code <= 419) return 'string';
  if (code >= 999 && code <= 1009) return 'string';
  if (code >= 1010 && code <= 1059) return 'number';
  if (code >= 1060 && code <= 1071) return 'int';
  return 'string';
}

/** 바이너리 DXF 인지 판별. 선두 22바이트가 고정 서명이다. */
export const BINARY_DXF_SENTINEL = 'AutoCAD Binary DXF\r\n ';

export function isBinaryDxf(buf: ArrayBuffer): boolean {
  if (buf.byteLength < BINARY_DXF_SENTINEL.length) return false;
  const head = new Uint8Array(buf, 0, BINARY_DXF_SENTINEL.length);
  for (let i = 0; i < BINARY_DXF_SENTINEL.length; i++) {
    if (head[i] !== BINARY_DXF_SENTINEL.charCodeAt(i)) return false;
  }
  return true;
}

/**
 * ASCII DXF 텍스트를 그룹 코드 쌍으로 자른다.
 *
 * 깨진 줄을 만나도 던지지 않고 건너뛴다 — 실무 도면에는 흔히 잡음이 섞여 있고,
 * 파일 하나 때문에 전체 변환이 실패하면 사용자는 원인을 알 수 없다.
 * 건너뛴 줄 수는 호출자에게 알려 경고로 노출한다.
 */
export function tokenize(text: string): { pairs: DxfPair[]; skipped: number } {
  // BOM 제거
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const lines = src.split(/\r\n|\r|\n/);

  const pairs: DxfPair[] = [];
  let skipped = 0;

  for (let i = 0; i + 1 < lines.length; i += 2) {
    const codeRaw = lines[i]!.trim();
    if (codeRaw === '') {
      // 빈 줄은 쌍을 깨뜨린다. 한 줄만 건너뛰고 정렬을 되찾는다.
      i -= 1;
      skipped++;
      continue;
    }

    const code = Number(codeRaw);
    if (!Number.isInteger(code)) {
      i -= 1; // 한 줄만 밀어 다시 맞춰본다
      skipped++;
      continue;
    }

    // 값 줄은 trim 하지 않는다 — TEXT 내용의 공백은 의미가 있다.
    // 단, 줄 끝 캐리지리턴만 제거한다(split 이 이미 처리하지만 방어적으로).
    pairs.push({ code, value: lines[i + 1]!.replace(/\r$/, '') });
  }

  return { pairs, skipped };
}

/** 값 줄을 숫자로. 실패 시 fallback. */
export function num(value: string, fallback = 0): number {
  const v = Number(value.trim());
  return Number.isFinite(v) ? v : fallback;
}

export function int(value: string, fallback = 0): number {
  const v = Number.parseInt(value.trim(), 10);
  return Number.isFinite(v) ? v : fallback;
}

/* -------------------------------------------------------------------------- *
 * 쓰기
 * -------------------------------------------------------------------------- */

/**
 * DXF 출력 버퍼.
 *
 * AutoCAD 관례를 따른다:
 *   · 그룹 코드는 폭 3으로 우측 정렬 (필수는 아니지만 관례이고 diff 가 읽기 쉽다)
 *   · 줄바꿈은 CRLF
 *   · 실수는 소수점 이하 최대 12자리, 불필요한 0 은 제거
 */
export class DxfWriterBuffer {
  private out: string[] = [];

  pair(code: number, value: string | number): this {
    const kind = valueKindOf(code);
    let v: string;

    if (typeof value === 'number') {
      v = kind === 'number' ? formatReal(value) : String(Math.round(value));
    } else {
      v = value;
    }

    this.out.push(String(code).padStart(3, ' '));
    this.out.push(v);
    return this;
  }

  /** 여러 쌍을 한 번에 */
  pairs(...list: [number, string | number][]): this {
    for (const [c, v] of list) this.pair(c, v);
    return this;
  }

  toString(): string {
    return this.out.join('\r\n') + '\r\n';
  }

  toBytes(): Uint8Array {
    return new TextEncoder().encode(this.toString());
  }
}

/**
 * 실수 표기.
 *
 * ★ 왕복 무결성의 핵심 지점이다.
 *   내부 좌표는 정수 µm 이고 DXF 에는 mm 실수로 나간다 (÷1000).
 *   지수 표기(1e-7)나 자릿수 손실이 생기면 다시 읽었을 때 좌표가 어긋난다.
 *   µm 정수를 1000 으로 나눈 값은 소수점 이하 최대 3자리이므로 3자리면 무손실이다.
 *   여유를 둬 6자리까지 쓰고, 뒤따르는 0 은 제거한다.
 */
export function formatReal(v: number): string {
  if (!Number.isFinite(v)) return '0.0';
  if (Number.isInteger(v)) return `${v}.0`;

  let s = v.toFixed(6);
  // 뒤따르는 0 제거하되 소수점 뒤 최소 1자리는 남긴다
  s = s.replace(/(\.\d*?)0+$/, '$1');
  if (s.endsWith('.')) s += '0';
  return s;
}
